import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { generateInitialProtocol, type ProtocolGenerationPhase } from '../lib/generateInitialProtocol'
import { resetCalibrationState } from '../lib/calibration/resetCalibrationState'
import { protocolWasPersisted } from '../lib/protocolIntegrity'
import { useUserLanguage } from '../hooks/useUserLanguage'
import { ONBOARDING_REVIEW_STEP_INDEX } from '../lib/onboarding'
import { ensureProfile, type ProfileRow } from '../lib/profile'
import { supabase } from '../lib/supabaseClient'
import Loading from './Loading'
import HealthScreeningStep from '../components/onboarding/HealthScreeningStep'
import StepWelcome from './onboarding/StepWelcome'
import Step0 from './onboarding/Step0'
import Step1 from './onboarding/Step1'
import Step2 from './onboarding/Step2'
import Step3 from './onboarding/Step3'
import Step4 from './onboarding/Step4'
import Step5 from './onboarding/Step5'
import Step6 from './onboarding/Step6'
import Step8 from './onboarding/Step8'

const profileFieldsFromRow = (data: Record<string, unknown>) => ({
  first_name: data.first_name,
  last_name: data.last_name,
  dob: data.dob,
  sex: data.sex,
  height_cm: data.height_cm,
  current_weight_kg: data.current_weight_kg,
  training_goal: data.training_goal,
  training_age: data.training_age,
  orthopedic_injury_inventory: data.orthopedic_injury_inventory,
  calibration_status: data.calibration_status,
  environment: data.environment,
  experience_level: data.experience_level,
  training_split: data.training_split,
  weekly_commitment: data.weekly_commitment,
  desk_bound: data.desk_bound,
  meal_prep_experience: data.meal_prep_experience,
  meal_prep_day: data.meal_prep_day,
  rest_days: data.rest_days,
  preferred_workout_start: data.preferred_workout_start,
  medical_niggles: data.medical_niggles,
  past_injuries: data.past_injuries,
  equipment_available: data.equipment_available,
  medications: data.medications,
  body_fat_archetype: data.body_fat_archetype,
  allergies: data.allergies,
  food_preferences: data.food_preferences,
  vegetarian: data.vegetarian,
  meal_preference: data.meal_preference,
  cooking_skill_level: data.cooking_skill_level,
  cook_time_available: data.cook_time_available,
  kitchen_equipment: data.kitchen_equipment,
  supplement_stack: data.supplement_stack,
  meal_frequency: data.meal_frequency,
  eating_schedule_type: data.eating_schedule_type,
  fasting_preference: data.fasting_preference,
  fasting_window: data.fasting_window,
  street_address: data.street_address,
  zipcode: data.zipcode,
  city: data.city,
  user_lat: data.user_lat,
  user_lng: data.user_lng,
  nearby_stores: data.nearby_stores,
  shopping_preference: data.shopping_preference,
  weekly_budget_value: data.weekly_budget_value,
  preferred_currency: data.preferred_currency,
  country_code: data.country_code,
  currency_code: data.currency_code,
  currency_symbol: data.currency_symbol,
  preferred_retailer_id: data.preferred_retailer_id,
  preferred_store_name: data.preferred_store_name,
  timezone: data.timezone,
  health_screening_passed: data.health_screening_passed,
  health_screening_exclusions: data.health_screening_exclusions,
  health_screening_completed_at: data.health_screening_completed_at,
  disclaimer_accepted_at: data.disclaimer_accepted_at,
})

const Onboarding = () => {
  const { t } = useTranslation()
  const [step, setStep] = useState(0)
  const [allData, setAllData] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [generationPhase, setGenerationPhase] = useState<ProtocolGenerationPhase | null>(null)
  const [saveError, setSaveError] = useState('')
  const navigate = useNavigate()
  const [userId, setUserId] = useState<string | null>(null)

  const { loading: languageLoading, changeLanguage, language } = useUserLanguage(userId)

  useEffect(() => {
    let mounted = true

    const loadUserProfile = async () => {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
      if (!mounted) return
      if (sessionError || !sessionData.session?.user) {
        navigate('/')
        return
      }

      const user = sessionData.session.user
      setUserId(user.id)

      const { profile, error: profileError } = await ensureProfile(user)
      if (!mounted) return
      if (profileError || !profile) {
        setSaveError(profileError ?? t('app.onboarding.loadFailed'))
        setLoading(false)
        return
      }

      setAllData(profileFieldsFromRow(profile))

      if (profile.onboarding_completed) {
        if (!profile.paywall_dismissed_at) {
          navigate('/paywall')
        } else {
          navigate('/app?tab=coach')
        }
        return
      }

      setStep(Math.min(Math.max(profile.onboarding_step ?? 0, 0), ONBOARDING_REVIEW_STEP_INDEX))
      setLoading(false)
    }

    loadUserProfile()
    return () => {
      mounted = false
    }
  }, [navigate])

  const persistStep = async (stepIndex: number) => {
    const { data: sessionData } = await supabase.auth.getSession()
    if (!sessionData.session?.user) return
    await supabase.from('profiles').update({ onboarding_step: stepIndex }).eq('id', sessionData.session.user.id)
  }

  const handleWelcomeNext = () => {
    setStep(1)
    void persistStep(1)
  }

  const handleStepComplete = async (stepData: Record<string, unknown>) => {
    setSaveError('')
    const updatedData = { ...allData, ...stepData }
    setAllData(updatedData)

    const { data: sessionData } = await supabase.auth.getSession()
    if (!sessionData.session?.user) return

    const user = sessionData.session.user
    const { error: ensureError } = await ensureProfile(user)
    if (ensureError) {
      setSaveError(ensureError)
      return
    }

    const nextStepIndex = Math.min(step + 1, ONBOARDING_REVIEW_STEP_INDEX)
    const { error } = await supabase
      .from('profiles')
      .update({ onboarding_step: nextStepIndex, ...stepData })
      .eq('id', user.id)

    if (error) {
      console.error('Onboarding save failed:', error)
      setSaveError(error.message || t('app.onboarding.saveFailed'))
      return
    }

    if (step < ONBOARDING_REVIEW_STEP_INDEX) {
      setStep(step + 1)
    }
  }

  const handlePrevious = () => {
    if (step <= 0) return
    const prev = step - 1
    setStep(prev)
    void persistStep(prev)
  }

  const handleLaunch = async () => {
    setSaveError('')
    setGenerationPhase(null)
    setIsSubmitting(true)

    const { data: sessionData } = await supabase.auth.getSession()
    if (!sessionData.session?.user) {
      setIsSubmitting(false)
      return
    }

    const user = sessionData.session.user
    const { profile, error: ensureError } = await ensureProfile(user)
    if (ensureError || !profile) {
      setSaveError(ensureError ?? t('app.errors.profileLoad'))
      setIsSubmitting(false)
      return
    }

    const mergedProfile = { ...profile, ...allData } as ProfileRow

    const { error: resetError } = await resetCalibrationState(user.id)
    if (resetError) {
      setSaveError(resetError)
      setIsSubmitting(false)
      return
    }

    const { error: genError } = await generateInitialProtocol(user.id, mergedProfile, (phase) => {
      setGenerationPhase(phase)
    })
    if (genError) {
      setSaveError(genError)
      setGenerationPhase(null)
      setIsSubmitting(false)
      return
    }

    if (!(await protocolWasPersisted(mergedProfile.weekly_commitment))) {
      setSaveError(t('app.errors.connectionIssue'))
      setGenerationPhase(null)
      setIsSubmitting(false)
      return
    }

    const { error } = await supabase
      .from('profiles')
      .update({
        onboarding_completed: true,
        onboarding_step: ONBOARDING_REVIEW_STEP_INDEX,
        calibration_status: 'not_started',
        calibration_completed_sessions: 0,
      })
      .eq('id', user.id)

    if (error) {
      setSaveError(t('app.onboarding.saveFailed'))
      setIsSubmitting(false)
      return
    }

    navigate('/paywall')
  }

  if (loading || languageLoading) return <Loading />

  const stepProps = { onNext: handleStepComplete, onPrevious: handlePrevious, initialData: allData }

  const steps = [
    <StepWelcome key="welcome" onNext={handleWelcomeNext} language={language} onLanguageChange={changeLanguage} />,
    <Step0 key="step0" {...stepProps} stepIndex={1} />,
    <HealthScreeningStep key="health" {...stepProps} stepIndex={2} />,
    <Step1 key="step1" {...stepProps} stepIndex={3} />,
    <Step2 key="step2" {...stepProps} stepIndex={4} />,
    <Step3 key="step3" {...stepProps} stepIndex={5} />,
    <Step4 key="step4" {...stepProps} stepIndex={6} />,
    <Step5 key="step5" {...stepProps} stepIndex={7} />,
    <Step6 key="step6" {...stepProps} stepIndex={8} />,
    <Step8 key="step8" onLaunch={handleLaunch} onPrevious={handlePrevious} allData={allData} isSubmitting={isSubmitting} launchError={saveError} generationPhase={generationPhase} stepIndex={9} />,
  ]

  return (
    <>
      {saveError && step !== ONBOARDING_REVIEW_STEP_INDEX ? (
        <div className="fixed inset-x-0 top-[7.25rem] z-40 mx-auto max-w-lg px-4">
          <p className="rounded-2xl border border-kilo-danger/30 bg-kilo-danger-bg px-4 py-3 text-sm text-kilo-danger shadow-lg">
            {saveError}
          </p>
        </div>
      ) : null}
      <div key={step}>{steps[step]}</div>
    </>
  )
}

export default Onboarding
